import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Client,
  ClientDetailResponse,
  ClientsListResponse,
  ClientStatus,
} from '@stfors/shared-types';
import { api } from '../client';

export interface PhoneEntryInput {
  phone_type: string;
  phone: string;       // 10-digit raw form expected by the API
}

export interface CreateClientInput {
  /** 'individual' (default) or 'company'. Migration 0030. */
  kind?: 'individual' | 'company';
  // Structured contact-name parts. last_name is required for individuals
  // (server-enforced); optional for companies (the contact is optional).
  title?: string;
  first_name?: string;
  last_name?: string;
  suffix?: string;
  /** Company name — creates/links the organization for a company client. */
  company_name?: string;
  /** Link an existing organization instead of creating one (company kind). */
  organization_id?: number;
  email?: string;
  /** Multi-phone — first entry is treated as the primary. */
  phones?: PhoneEntryInput[];
  address?: string;
  industry?: string;
  notes?: string;
  tags?: string[];
  status?: ClientStatus;
}

export function useClients() {
  return useQuery({
    queryKey: ['clients'],
    queryFn: ({ signal }) => api.get<ClientsListResponse>('/api/clients', signal),
  });
}

export function useClient(id: number | null) {
  return useQuery({
    queryKey: ['clients', { id }],
    queryFn: ({ signal }) =>
      api.get<ClientDetailResponse>(`/api/clients/${id}`, signal),
    enabled: id !== null,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClientInput) =>
      api.post<{ client: Client }>('/api/clients', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export interface UpdateClientInput {
  status?: ClientStatus;
  /** The client's default representative — law firm + attorney (0054). */
  rep_organization_id?: number | null;
  rep_person_id?: number | null;
  // All four name parts patchable independently. last_name when present
  // must be non-empty (enforced server-side); other parts can be cleared
  // by sending null.
  title?: string | null;
  first_name?: string | null;
  last_name?: string;
  suffix?: string | null;
  company_name?: string | null;
  // The company's OWN email/phone (company clients) — write the linked org.
  // (A company client's address is the org's structured address, edited via
  // the organizations PATCH, not here.)
  company_email?: string | null;
  company_phone?: string | null;   // 10-digit raw form
  /** Reassign a company client's primary contact to an existing person; null
   *  clears the pointer. Keeps organization_contacts.is_primary in sync. */
  primary_contact_person_id?: number | null;
  email?: string | null;
  /** When present, replaces the entire phone list (delete-all + insert). */
  phones?: PhoneEntryInput[];
  /** Legacy single-line address + structured fields (individual clients). */
  address?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  industry?: string | null;
  notes?: string | null;
  tags?: string[];
}

export function useUpdateClient(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateClientInput) =>
      api.patch<{ client: Client }>(`/api/clients/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
      // Company edits write the linked organization — refresh org list + any
      // open org detail page (keyed ['organization', id]) too.
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      void qc.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}

/** Soft-delete a client role row (does not delete the person/org). */
export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/clients/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
      // Role badges on person/org detail pages re-resolve from these keys.
      void qc.invalidateQueries({ queryKey: ['person'] });
      void qc.invalidateQueries({ queryKey: ['organization'] });
      void qc.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}
